import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { VStack, HStack } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import { Modal } from "../../../../components/core/basic/Modal";
import AssetSelector from "../../../../components/core/blocks/AssetSelector/AssetSelector";
import AssetSummary from "../../../../components/core/blocks/AssetSelector/AssetSummary";
import { TextField } from "../../../../components/fields/base/TextField";
import { useAppSelector } from "../../../../redux/hooks";
import { AdGroupSelector } from "../AdGroupSelector";
import type { SpotlightVideo } from "./SpotlightVideoSummary";

export type SpotlightVideoFormData = {
  assetId: string;
  assetVersion: string;
  headline: string;
  description: string;
  adGroups: string[];
  customizeAdGroups: boolean;
};

export const SpotlightVideoFormModal = NiceModal.create(
  ({
    asin,
    adId,
    video,
    onSubmit,
  }: {
    asin: string;
    adId: number;
    video?: SpotlightVideo;
    onSubmit: (data: SpotlightVideoFormData) => Promise<void>;
  }) => {
    const modal = useModal();
    const { marketPlace } = useAppSelector((state) => state.marketPlace);
    const { t } = useTranslation(["advertising", "common"]);

    const {
      control,
      handleSubmit,
      setValue,
      formState: { isValid, isSubmitting },
    } = useForm<SpotlightVideoFormData>({
      mode: "onChange",
      defaultValues: {
        assetId: video?.assetId ?? "",
        assetVersion: video?.assetVersion ?? "",
        headline: video?.headline ?? "",
        description: video?.description ?? "",
        adGroups: video?.adGroupIds ?? [],
        customizeAdGroups: (video?.adGroupIds?.length ?? 0) > 0,
      },
    });

    const adGroups = useWatch({ name: "adGroups", control });
    const customizeAdGroups = useWatch({ name: "customizeAdGroups", control });
    const headline = useWatch({ name: "headline", control });
    const description = useWatch({ name: "description", control });
    const assetId = useWatch({ name: "assetId", control });

    const defaultFilters = useMemo(
      () => ({
        sponsored_product_spotlight_videos: true,
      }),
      [],
    );

    return (
      <Modal
        isOpen={modal.visible}
        isDismissable={!isSubmitting}
        onOpenChange={(isOpen) => !isOpen && !isSubmitting && modal.remove()}
        title={
          video ?
            t(($) => $.advertising["Edit Spotlight Video for {asin}"], { asin })
          : t(($) => $.advertising["Create a New Spotlight Video for {asin}"], { asin })
        }
        css={{ width: { base: "90vw", lg: "1000px" } }}
      >
        <VStack gap="medium" paddingX="large" paddingBottom="large" overflow="auto">
          {assetId ?
            <VStack gap="small" alignItems="flex-start" width="100%">
              <AssetSummary
                assetId={assetId}
                assetDescriptor={t(($) => $.advertising["Spotlight Video"])}
                assetType="VIDEO"
                hidden={false}
                summaryContainerClasses=""
                displayAssetName={false}
                columnSpan={12}
              />
              <Button
                onPress={() => {
                  setValue("assetId", "");
                }}
                size={"small"}
              >
                {t(($) => $.advertising["Change Video"])}
              </Button>
            </VStack>
          : <div className={css({ width: "100%" })}>
              <AssetSelector
                marketPlace={{ marketPlace }}
                state={{}}
                products={[]}
                selected={[]}
                selections={[]}
                allowedAssetTypes={["VIDEO"]}
                initialTypeFilter="VIDEO"
                hideAssetTypeFilter
                hidePreviewField
                hideEligibilityFilter
                hideTagsFilter
                hideAdvancedFilters
                hideGeneratedAssetsFilter
                hideSelectionControls={false}
                multiSelect={false}
                defaultPageSize={8}
                defaultFilters={defaultFilters}
                handleAssetSelection={(selection: string[] | string | null, asset: Record<string, unknown> | null) => {
                  const id = Array.isArray(selection) ? selection[0] : selection;
                  setValue("assetId", id ?? "", { shouldValidate: true, shouldDirty: true });
                  if (asset) {
                    setValue("assetVersion", typeof asset.version === "string" ? asset.version : "version_v1");
                  } else {
                    setValue("assetVersion", "");
                  }
                }}
              />
            </div>
          }

          {assetId && (
            <form
              onSubmit={handleSubmit(async (data) => {
                try {
                  await onSubmit(data);
                  modal.remove();
                } catch {
                  // error handled by mutation's onError toast; keep modal open for retry
                }
              })}
              className={css({ width: "100%" })}
            >
              <VStack>
                <Controller
                  name="headline"
                  control={control}
                  rules={{
                    required: { value: true, message: t(($) => $.advertising["Feature name is required"]) },
                    maxLength: {
                      value: 15,
                      message: t(($) => $.advertising["Feature name must be 15 characters or fewer"]),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <VStack paddingTop="medium" width="100%">
                      <TextField
                        label={t(($) => $.advertising["Feature Name"])}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        maxLength={15}
                        errorMessage={fieldState.error?.message}
                        css={{ width: "100%" }}
                      />
                      <small className={css({ width: "100%", fontSize: "xsmall", paddingX: "small" })}>
                        {t(($) => $.advertising["{count} / 15 characters"], { count: headline.length })}
                      </small>
                    </VStack>
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: { value: true, message: t(($) => $.advertising["Feature description is required"]) },
                    maxLength: {
                      value: 55,
                      message: t(($) => $.advertising["Feature description must be 55 characters or fewer"]),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <VStack paddingY="medium" width="100%">
                      <TextField
                        label={t(($) => $.advertising["Feature Description"])}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        maxLength={55}
                        errorMessage={fieldState.error?.message}
                        css={{ width: "100%", marginY: 0 }}
                      />
                      <small className={css({ width: "100%", fontSize: "xsmall", paddingX: "small" })}>
                        {t(($) => $.advertising["{count} / 55 characters"], { count: description.length })}
                      </small>
                    </VStack>
                  )}
                />

                <AdGroupSelector
                  adId={adId}
                  selectedAdGroupIds={adGroups}
                  isExpanded={customizeAdGroups}
                  onExpandedChange={(expanded) => {
                    setValue("customizeAdGroups", expanded);
                    if (!expanded) setValue("adGroups", []);
                  }}
                  onSelectionChange={(ids) => setValue("adGroups", ids)}
                />

                <HStack gap="medium">
                  <Button variant="secondary" onPress={() => modal.remove()} isDisabled={isSubmitting}>
                    {t(($) => $.common.actions.Cancel)}
                  </Button>
                  <Button type="submit" isDisabled={!isValid || isSubmitting}>
                    {isSubmitting ?
                      t(($) => $.common["Saving..."])
                    : video ?
                      t(($) => $.advertising["Save Changes"])
                    : t(($) => $.advertising["Add Spotlight Video"])}
                  </Button>
                </HStack>
              </VStack>
            </form>
          )}
        </VStack>
      </Modal>
    );
  },
);
